import { catalogLabel, packagingLabel, skuVarietyLabel } from '@/i18n/catalog';
import type { BuyerSpuVO, BuyerSkuVO } from '@/api/productApi';
import type { Lang } from '@/i18n/dict';
import { emojiOf } from './productAdapter';

/** 买家端展示用 SPU（商品级） */
export interface BuyerSpu {
  id: string;
  name: string;
  /** 英文名（EN 模式展示用；中文值恒在 name） */
  nameEn?: string;
  /** 分类名称 */
  category: string;
  categoryZh?: string;
  /** 分类英文名（搜索过滤用） */
  categoryEn?: string;
  categoryId?: string;
  emoji: string;
  coverImageUrl?: string | null;
  /** 品类封面图（SPU 无封面时的回退图，再回退 emoji） */
  categoryCoverImageUrl?: string | null;
  imageUrls: string[];
  description?: string;
  /** 该 SPU 下可选规格数量 */
  skuCount: number;
  skus: BuyerSkuItem[];
  /** 行情裸价区间（CNY/吨，后端聚合；全缺为 null） */
  priceMin?: number | null;
  priceMax?: number | null;
}

/** 买家端展示用 SKU（规格行） */
export interface BuyerSkuItem {
  /** = product.id，下单/询盘携带此 id */
  id: string;
  name: string;
  variety?: string;
  specification?: string;
  packagingSpec?: string;
  /** 规格展示文案：品种 + 规格 + 包装（如「纯白 · 5.5+ · 10kg/箱」） */
  specLabel: string;
  /** 是否唯一规格（单 SKU 时无需选择） */
  sole: boolean;
}

/** 按语言取值：非中文界面优先英文字段（韩文暂无独立字段，用英文），空/缺失回退中文 */
export function pickByLang(lang: Lang, zh?: string | null, en?: string | null): string {
  if (lang !== 'zh' && en && en.trim() !== '') return en;
  return zh ?? '';
}

function parseImageUrls(raw?: string | null): string[] {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(arr)) return arr.filter((x: unknown) => typeof x === 'string');
  } catch {
    /* ignore */
  }
  return [];
}

/** 由品种/规格/包装拼出规格展示文案（lang=en 时品种/规格取英文字段） */
export function buildSpecLabel(s: BuyerSkuVO, lang: Lang = 'zh'): string {
  const variety = skuVarietyLabel(lang, s.variety, s.varietyEn);
  const spec = catalogLabel(lang, s.specification, s.specificationEn);
  const parts = [variety, spec, packagingLabel(lang, s.packagingSpec)].filter((x) => !!x && x.trim() !== '');
  return parts.length ? parts.join(' · ') : pickByLang(lang, s.name);
}

export function toDisplaySpu(spu: BuyerSpuVO, lang: Lang = 'zh'): BuyerSpu {
  const skus = (spu.skus ?? []).map((s) => ({
    id: s.id,
    name: catalogLabel(lang, s.name),
    variety: skuVarietyLabel(lang, s.variety, s.varietyEn),
    specification: catalogLabel(lang, s.specification, s.specificationEn),
    packagingSpec: packagingLabel(lang, s.packagingSpec),
    specLabel: buildSpecLabel(s, lang),
    sole: false,
  }));
  if (skus.length === 1) skus[0].sole = true;
  return {
    id: spu.id,
    name: lang === 'ko' ? catalogLabel(lang, spu.name, spu.nameEn) : pickByLang(lang, spu.name, spu.nameEn),
    nameEn: spu.nameEn,
    category: catalogLabel(lang, spu.categoryName, spu.categoryNameEn),
    categoryZh: spu.categoryName ?? '',
    categoryEn: spu.categoryNameEn,
    categoryId: spu.categoryId,
    emoji: emojiOf(spu.name, spu.categoryName),
    coverImageUrl: spu.coverImageUrl || undefined,
    categoryCoverImageUrl: spu.categoryCoverImageUrl || undefined,
    imageUrls: parseImageUrls(spu.imageUrls),
    description: pickByLang(lang, spu.description, spu.descriptionEn),
    skuCount: spu.skuCount ?? skus.length,
    skus,
    priceMin: spu.priceMin ?? null,
    priceMax: spu.priceMax ?? null,
  };
}

export function toDisplaySpus(list: BuyerSpuVO[], lang: Lang = 'zh'): BuyerSpu[] {
  return (list ?? []).map((s) => toDisplaySpu(s, lang));
}
